/* eslint-disable no-template-curly-in-string */
/* eslint-disable react-hooks/exhaustive-deps */
//import packages
import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Layout,
  Form,
  Input,
  Button,
  Row,
  Col,
  Typography,
  message,
  Spin,
  Alert,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import jwtDecode from 'jwt-decode';

//import CSS
import "./ResetPasswordComponent.css";

//import icons
import LogoIcon from "../../assets/icons/LogoIcon";

//import functions
import PhysicianApi from "../../axios/physician/PhysicianApi";
import { isValidPassword, PasswordChecker } from "../PasswordChecker/PasswordChecker";

const { Content } = Layout;
const { Title } = Typography;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ResetPasswordComponent = () => {
  const history = useHistory();
  let query = useQuery();
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [newPassword, setNewPassword] = useState('');

  const layout = {
    labelCol: {
      span: 12,
    },
    wrapperCol: {
      span: 24,
    },
  };

  const validateMessages = {
    required: "Please enter your ${label}",
    types: {
      email: "${label} is not valid email!",
    },
  };

  const redirectToLogin = (type, displayMessage) => {
    message[type](displayMessage);
    setTimeout(() => {
      history.push("/login");
    }, 2000);
  };

  const decodeResetToken = () => {
    const resetToken = localStorage.getItem('resetToken');
    let decodedResetToken = {};
    try {
        decodedResetToken = jwtDecode(resetToken);
    } catch (error) {
        console.log("=========================== Reset Token decoding error ==========================", error);
        return {};
    }
    return decodedResetToken;
}

  const onFinish = (values) => {
    setLoading(true);
    const { password, confirmPassword } = values;
    PhysicianApi.passwordReset({
      password,
      confirmPassword,
    })
      .then((res) => {
        const { data } = res;
        if (data && data.success) {
          message.success(
            "Successfully Updated your password! Please login using the new password"
          );
          localStorage.clear();
          redirectToLogin(
            "success",
            "You will get redirected to the login page soon!"
          );
        } else {
          if (data && data.message) {
            message.error(data.message);
          } else {
            message.error(
              "Failed reset password! Please make sure your reset password request is valid"
            );
          }
          setLoading(false);
        }
      })
      .catch((error) => {
        console.log("================== error ========================", error);
        const { response } = error;
        if (response) {
          const { data } = response;
          if (data && data.message) {
            message.error(data.message);
          } else {
            message.error(
              "Failed reset password! Please make sure your reset password request is valid"
            );
          }
        } else {
          message.error(
            "Failed reset password! Please make sure your reset password request is valid"
          );
        }
        setLoading(false);
      });
  };

  const handlePasswordResetCancel = () => {
    history.push("/login");
  };

  const tokenVerification = () => {
    console.log(
      "================================= token verification ================================"
    );
    const token = query.get("webtoken");
    if (token) {
      localStorage.setItem("resetToken", token);
      history.replace("/reset-password");
    } else {
      const resetTokenFromLocalStorage = localStorage.getItem("resetToken");
      if (!resetTokenFromLocalStorage) {
        message.error("We can't identify your password reset request");
        localStorage.clear();
        redirectToLogin(
          "error",
          "You will be redirected to the login page soon!"
        );
        return false;
      }
    }
    const decodedResetToken = decodeResetToken();
    const { user } = decodedResetToken;
    if (user && user.type && user.name) {
      setUsername(user.name);
    } else {
      message.error("We can't identify your password reset request");
      redirectToLogin(
        "error",
        "You will be redirected to the login page soon!"
      );
    }
  };

  useEffect(() => {
    tokenVerification();
    return () => {
      localStorage.clear();
    };
  }, []);

  return (
    <Layout className="reset-password-component-layout">
      <Content className="reset-password-component-content">
        <div className="reset-password-component-wrapper">
          <Spin spinning={loading} size="large" indicator={<LoadingOutlined />}>
            <Form
              {...layout}
              layout={"vertical"}
              name="login-form"
              onFinish={onFinish}
              validateMessages={validateMessages}
            >
              <Title className="reset-password-component-title" level={2}>
                <LogoIcon width="224px" height="80px" />
              </Title>
              <Title className="reset-password-component-title-level" level={3}>
                Reset Password
              </Title>
              {username && (
                <Alert
                  className="reset-password-component-alert"
                  message={`Hello! ${username}`}
                  type="success"
                />
              )}
              <PasswordChecker password={newPassword} />
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item
                    name="password"
                    label="New Password"
                    rules={[
                      {
                        required: true,
                        message: "Please input your password!",
                      },
                    ]}
                    hasFeedback
                  >
                    <Input.Password
                      id="reset-password-new-input"
                      size="large"
                      placeholder="Enter new password"
                      onChange={e => setNewPassword(e.target.value)}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="confirmPassword"
                    label="Confirm New Password"
                    dependencies={["password"]}
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        message: "Please confirm your password!",
                      },
                      ({ getFieldValue }) => ({
                        validator(rule, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }

                          return Promise.reject(
                            "The two passwords that you entered do not match!"
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      id="reset-password-confirm-input"
                      size="large"
                      placeholder="Re-enter the new password"
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    className="reset-password-component-form-item"
                    wrapperCol={{ ...layout.wrapperCol }}
                  >
                    <Button
                      id="reset-password-cancel-btn"
                      className="reset-password-component-button"
                      size="large"
                      type="secondary"
                      onClick={handlePasswordResetCancel}
                    >
                      Cancel
                    </Button>
                    <Button
                      id="reset-password-submit-btn"
                      className="reset-password-component-button"
                      size="large"
                      type="primary"
                      htmlType="submit"
                      disabled={!isValidPassword(newPassword)}
                    >
                      Submit
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Spin>
        </div>
      </Content>
    </Layout>
  );
};

export default ResetPasswordComponent;
